import { ToJson } from '../interfaces/ToJson';
import { AddressChoice } from '@/types';

/**
 * Interface représentant un psychologue utilisé pour la recherche publique
 */
export interface ISearchPsychologist {
	id: number;
	firstname: string;
	lastname: string;
	address: string;
	coordinates_x?: number;
	coordinates_y?: number;
	address_additional?: string;
	second_address?: string;
	second_address_coordinates_x?: number;
	second_address_coordinates_y?: number;
	second_address_additional?: string;
	visible: boolean;
	public: string;
	teleconsultation: boolean;
	languages: string;
	display_phone: boolean;
	phone: string;
	display_email: boolean;
	email?: string;
	website?: string;
	origin?: AddressChoice;
	distance?: number;
}

/**
 * Classe représentant un psychologue utilisé pour la recherche publique
 */
export class SearchPsychologist implements ToJson<ISearchPsychologist> {
	private _id: number;
	private _firstname: string;
	private _lastname: string;
	private _fullname: string;
	private _address: string;
	private _coordinates_x?: number;
	private _coordinates_y?: number;
	private _address_additional?: string;
	private _secondAddress?: string;
	private _second_address_coordinates_x?: number;
	private _second_address_coordinates_y?: number;
	private _second_address_additional?: string;
	private _visible: boolean;
	private _public: string;
	private _teleconsultation: boolean;
	private _languages: string;
	private _display_phone: boolean;
	private _phone: string;
	private _display_email: boolean;
	private _email?: string;
	private _website?: string;
	private _origin?: AddressChoice;
	private _distance?: number;
	private _idByAddress: string;

	/**
	 * Constructeur de la classe PublicSearchPsychologist
	 * @param data Interface IPublicSearchPsychologist
	 */
	constructor(data: ISearchPsychologist) {
		this._id = data.id;
		this._firstname = data.firstname;
		this._lastname = data.lastname;
		this._fullname = this.getFullName();
		this._address = data.address;
		this._coordinates_x = data.coordinates_x;
		this._coordinates_y = data.coordinates_y;
		this._address_additional = data.address_additional;
		this._secondAddress = data.second_address;
		this._second_address_coordinates_x = data.second_address_coordinates_x;
		this._second_address_coordinates_y = data.second_address_coordinates_y;
		this._second_address_additional = data.second_address_additional;
		this._visible = data.visible;
		this._public = data.public;
		this._teleconsultation = data.teleconsultation;
		this._languages = data.languages;
		this._display_phone = data.display_phone;
		this._phone = data.phone;
		this._display_email = data.display_email;
		this._email = data.email;
		this._website = data.website;
		this._origin = data.origin;
		this._distance = data.distance;
		this._idByAddress = this.getIdByAddress();
	}

	/**
	 * Récupère le psychologue sous forme de JSON
	 */
	toJson(): ISearchPsychologist {
		return {
			id: this._id,
			firstname: this._firstname,
			lastname: this._lastname,
			address: this._address,
			coordinates_x: this._coordinates_x,
			coordinates_y: this._coordinates_y,
			address_additional: this._address_additional,
			second_address: this._secondAddress,
			second_address_coordinates_x: this._second_address_coordinates_x,
			second_address_coordinates_y: this._second_address_coordinates_y,
			second_address_additional: this._second_address_additional,
			visible: this._visible,
			public: this._public,
			teleconsultation: this._teleconsultation,
			languages: this._languages,
			display_phone: this._display_phone,
			phone: this._phone,
			display_email: this._display_email,
			email: this._email,
			website: this._website,
			origin: this._origin,
			distance: this._distance
		};
	}

	/**
	 * Récupère le nom complet du psychologue
	 * @returns Le nom complet du psychologue
	 */
	private getFullName(): string {
		return `${this._lastname} ${this._firstname}`;
	}

	/**
	 * Récupère l'id du psychologue en fonction de l'adresse
	 * @returns L'id du psychologue
	 */
	private getIdByAddress(): string {
		switch (this._origin) {
			case 'from_first_address':
				return `${this._id};${this._coordinates_x};${this._coordinates_y}`;
			case 'from_second_address':
				return `${this._id};${this._second_address_coordinates_x};${this._second_address_coordinates_y}`;
			default:
				return `${this._id}`;
		}
	}

	/**
	 * Récupère les coordonnées du psychologue en fonction de l'adresse de recherche
	 */
	public getCoordinatesByAddress(): number[] {
		if (this._origin === 'from_first_address') {
			return [this._coordinates_y ? this._coordinates_y : 0, this._coordinates_x ? this._coordinates_x : 0];
		} else if (this._origin === 'from_second_address') {
			return [this._second_address_coordinates_y ? this._second_address_coordinates_y : 0, this._second_address_coordinates_x ? this._second_address_coordinates_x : 0];
		} else {
			return [];
		}
	}

	/**
	 * Récupère l'id du psychologue
	 */
	public get id(): number {
		return this._id;
	}

	/**
	 * Récupère le prénom du psychologue
	 */
	public get firstname(): string {
		return this._firstname;
	}

	/**
	 * Récupère le nom du psychologue
	 */
	public get lastname(): string {
		return this._lastname;
	}

	/**
	 * Récupère le nom et prénom du psychologue
	 */
	public get fullname(): string {
		return this._fullname;
	}

	/**
	 * Récupère l'adresse du psychologue
	 */
	public get address(): string {
		return this._address;
	}

	/**
	 * Récupère l'adresse additionnelle du psychologue
	 */
	public get address_additional(): string | undefined {
		return this._address_additional;
	}

	/**
	 * Récupère l'adresse secondaire du psychologue
	 */
	public get secondAddress(): string | undefined {
		return this._secondAddress;
	}

	/**
	 * Récupère l'adresse secondaire additionnelle du psychologue
	 */
	public get second_address_additional(): string | undefined {
		return this._second_address_additional;
	}

	/**
	 * Récupère la visibilité du psychologue
	 */
	public get visible(): boolean {
		return this._visible;
	}

	/**
	 * Récupère le public du psychologue
	 */
	public get public(): string {
		return this._public;
	}

	/**
	 * Récupère la téléconsultation du psychologue
	 */
	public get teleconsultation(): boolean {
		return this._teleconsultation;
	}

	/**
	 * Récupère les langues du psychologue
	 */
	public get languages(): string {
		return this._languages;
	}

	/**
	 * Récupère le téléphone du psychologue
	 */
	public get phone(): string {
		return this._phone;
	}

	/**
	 * Récupère l'affichage du téléphone du psychologue
	 */
	public get display_phone(): boolean {
		return this._display_phone;
	}

	/**
	 * Récupère l'email du psychologue
	 */
	public get email(): string | undefined {
		return this._email;
	}

	/**
	 * Récupère l'affichage de l'email du psychologue
	 */
	public get display_email(): boolean {
		return this._display_email;
	}

	/**
	 * Récupère le site web du psychologue
	 */
	public get website(): string | undefined {
		return this._website;
	}

	/**
	 * Récupère l'origine du psychologue
	 */
	public get origin(): AddressChoice | undefined {
		return this._origin;
	}

	/**
	 * Récupère la distance du psychologue par rapport à l'adresse de recherche
	 */
	public get distance(): number | undefined {
		return this._distance;
	}

	/**
	 * Récupère l'id du psychologue en fonction de l'adresse
	 */
	public get idByAddress(): string {
		return this._idByAddress;
	}
}
